import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  findLeagueBySlug,
  findTeamBySlug,
  getSportBySlug,
  teamToSlug,
} from '../utils/sports';
import './LeagueTeamNav.css';

function getNavItems(sport, selectedLeague) {
  if (!sport) return [];

  if (sport.leagues?.length) {
    if (selectedLeague) {
      return (selectedLeague.teams || []).map(team => ({
        type: 'team',
        label: team,
        slug: teamToSlug(team),
      }));
    }

    return sport.leagues.map(league => ({
      type: 'league',
      label: league.name,
      slug: league.slug,
    }));
  }

  return (sport.teams || []).map(team => ({
    type: 'team',
    label: team,
    slug: teamToSlug(team),
  }));
}

function LeagueTeamNav({ sportSlug }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scrollRef = useRef(null);
  const sport = getSportBySlug(sportSlug);

  const leagueSlug = searchParams.get('league') || '';
  const teamSlug = searchParams.get('team') || '';
  const selectedLeague = findLeagueBySlug(sport, leagueSlug);
  const selectedTeam = findTeamBySlug(sport, teamSlug);

  const items = useMemo(
    () => getNavItems(sport, selectedLeague),
    [sport, selectedLeague]
  );

  useEffect(() => {
    const active = scrollRef.current?.querySelector('.sports-nav-pill.active');
    if (!active) return undefined;
    const id = window.requestAnimationFrame(() => {
      active.scrollIntoView({
        inline: 'center',
        block: 'nearest',
        behavior: 'smooth',
      });
    });
    return () => window.cancelAnimationFrame(id);
  }, [selectedLeague, selectedTeam, items]);

  if (!sport || !items.length) {
    return null;
  }

  const base = `/sports/${sport.slug}`;

  const handleClick = item => {
    if (item.type === 'league') {
      if (selectedLeague?.slug === item.slug) {
        navigate(base);
      } else {
        navigate(`${base}?league=${item.slug}`);
      }
      return;
    }

    if (selectedTeam && teamToSlug(selectedTeam) === item.slug) {
      if (selectedLeague) {
        navigate(`${base}?league=${selectedLeague.slug}`);
      } else {
        navigate(base);
      }
      return;
    }

    if (selectedLeague) {
      navigate(`${base}?league=${selectedLeague.slug}&team=${item.slug}`);
    } else {
      navigate(`${base}?team=${item.slug}`);
    }
  };

  return (
    <nav
      className="sports-nav sports-nav--editorial league-team-nav"
      aria-label={`${sport.name} leagues and teams`}
    >
      <div className="sports-nav-scroll" ref={scrollRef}>
        {selectedLeague && (
          <button
            type="button"
            className="sports-nav-pill"
            onClick={() => navigate(base)}
          >
            All leagues
          </button>
        )}

        {items.map(item => {
          const isActive =
            (item.type === 'league' && selectedLeague?.slug === item.slug) ||
            (item.type === 'team' && selectedTeam && teamToSlug(selectedTeam) === item.slug);

          return (
            <button
              key={`${item.type}-${item.slug}`}
              type="button"
              className={`sports-nav-pill${isActive ? ' active' : ''}`}
              onClick={() => handleClick(item)}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export default LeagueTeamNav;
